import { useEffect, useState } from 'react'

import { useTexts } from '../useTexts'

const LINEAR_INTRO_INTERVAL_MS = 10_000 // 10s per message
const PREP_LOOP_INTERVAL_MS = 20_000 // 20s per message
const STEP2_ROTATION_INTERVAL_MS = 20_000 // 20s per message

// A "rotation" is one full pass through the regular Preparation Loop messages.
// Every Nth full pass also appends "Finalizing the data environment…" at the end
// (an "almost there" beat).
const PREP_CYCLES_PER_FINALIZING = 3

// The backend signals near-completion at 97/98/99% — lock to the wrapping-up copy.
const WRAPPING_UP_THRESHOLD = 97

// Build the repeating Preparation Loop order: the regular messages cycle in order;
// after every `PREP_CYCLES_PER_FINALIZING` passes, "Finalizing" is appended once.
// e.g. [reg×4] [reg×4] [reg×4, finalizing] → repeat.
function buildPrepSequence(regular: readonly string[], finalizing: string): string[] {
  const sequence: string[] = []
  for (let cycle = 1; cycle <= PREP_CYCLES_PER_FINALIZING; cycle++) {
    sequence.push(...regular)
    if (cycle === PREP_CYCLES_PER_FINALIZING) sequence.push(finalizing)
  }
  return sequence
}

/**
 * Returns the text displayed next to the spinner / progress bar. Rotates on
 * timed intervals per the FE engineering analysis (page 9–10).
 *
 * Step 1 (`progress <= 50`):
 *   - Linear Intro: 4 messages, 10s each (one-shot, in order)
 *   - Preparation Loop: 4 messages, 20s each; every 3rd full pass ends with "Finalizing"
 *
 * Step 2 (`progress > 50`):
 *   - 8 messages on 20s rotation
 *   - At `progress >= 97`, lock to "Wrapping up the final plan…"
 *
 * The rotation cadence is independent of progress — if progress jumps past
 * 50% mid-intro, Step 2's rotation just takes over.
 */
export function useRotatingText(progress: number | 'completed'): string {
  const texts = useTexts()
  const step1Texts = useStep1Text()
  const step2Index = useStep2Index()

  if (progress === 'completed') return ''

  if (progress <= 50) return step1Texts

  if (progress >= WRAPPING_UP_THRESHOLD) return texts.step2WrappingUp

  return texts.step2Rotation[step2Index]
}

function useStep1Text(): string {
  const texts = useTexts()
  const [introIndex, setIntroIndex] = useState(0)
  const [loopRotation, setLoopRotation] = useState(0)

  // Linear Intro: tick through 4 messages then stop incrementing
  useEffect(() => {
    if (introIndex >= texts.step1LinearIntro.length) return
    const t = setInterval(() => {
      setIntroIndex(i => (i < texts.step1LinearIntro.length ? i + 1 : i))
    }, LINEAR_INTRO_INTERVAL_MS)
    return () => clearInterval(t)
  }, [introIndex, texts.step1LinearIntro.length])

  // Preparation Loop ticks while we're still in Step 1 after intro completes
  useEffect(() => {
    if (introIndex < texts.step1LinearIntro.length) return
    const t = setInterval(() => {
      setLoopRotation(n => n + 1)
    }, PREP_LOOP_INTERVAL_MS)
    return () => clearInterval(t)
  }, [introIndex, texts.step1LinearIntro.length])

  // Still inside Linear Intro
  if (introIndex < texts.step1LinearIntro.length) {
    return texts.step1LinearIntro[introIndex]
  }

  // Preparation Loop — regular messages cycle in order; every 3rd full pass ends
  // with "Finalizing the data environment…". `loopRotation` ticks once per message.
  const sequence = buildPrepSequence(texts.step1PreparationLoop, texts.step1PreparationFinalizing)
  return sequence[loopRotation % sequence.length]
}

function useStep2Index(): number {
  const texts = useTexts()
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const t = setInterval(() => {
      setIndex(i => (i + 1) % texts.step2Rotation.length)
    }, STEP2_ROTATION_INTERVAL_MS)
    return () => clearInterval(t)
  }, [texts.step2Rotation.length])

  return index
}
